import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeCompat } from '../contexts/ThemeContext';
import { 
  ArrowLeft,
  CaretDown,
  CheckCircle,
  XCircle,
  Question
} from 'phosphor-react';
import { 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import ClickableElement from './ClickableElement';
import { API_BASE_URL, clearRequestHeaders } from '../services/dataService';
import axios from 'axios';

interface Fight {
  fighter1: string;
  fighter2: string;
  winner: string;
  ourPrediction: string;
  bettingFavorite: string;
}

interface Event {
  eventName: string;
  eventDate: string;
  fights: Fight[];
}

interface Stats {
  totalEvents: number;
  totalFights: number;
  accuracy: number;
  favoriteAccuracy: number;
  modelROI?: number;
  favoriteROI?: number;
  modelPL?: number;
  favoritePL?: number;
  roiFights?: number;
}

interface TimelinePoint {
  date: string;
  successRate: number;
  baselineSuccessRate: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  const { theme } = useThemeCompat();
  
  if (active && payload && payload.length) {
    return (
      <div className={`rounded-lg px-3 py-2 ${theme === 'dark' ? 'bg-dark-bg-secondary' : 'bg-background-secondary'}`}>
        <p className="text-xs font-medium text-text-secondary dark:text-dark-text-secondary">
          {label ? new Date(label).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }) : ''}
        </p>
        <div className="mt-2 space-y-1">
          <p className="text-xs">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400 mr-1"></span>
            <span className="font-medium text-text-secondary dark:text-dark-text-secondary">Model: </span>
            <span className="text-text-primary dark:text-dark-text-primary">
              {payload[0]?.value?.toFixed(1)}%
            </span>
          </p>
          <p className="text-xs">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 dark:bg-red-400 mr-1"></span>
            <span className="font-medium text-text-secondary dark:text-dark-text-secondary">Baseline: </span>
            <span className="text-text-primary dark:text-dark-text-primary">
              {payload[1]?.value?.toFixed(1)}%
            </span>
          </p>
        </div>
      </div>
    );
  }

  return null;
};

const PredictionTracker: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useThemeCompat();
  const [events, setEvents] = useState<Event[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [timeline, setTimeline] = useState<TimelinePoint[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching prediction data from:', API_BASE_URL);
        
        // Use axios with the clearRequestHeaders utility
        const client = clearRequestHeaders();
        
        // Log debugging information
        console.log('API base URL:', API_BASE_URL);
        console.log('Endpoints:', 
          `${API_BASE_URL}/api/prediction-tracking`,
          `${API_BASE_URL}/api/prediction-tracking/stats`
        );
        
        // Use axios for better error handling
        const [eventsRes, statsRes] = await Promise.all([
          client.get(`${API_BASE_URL}/api/prediction-tracking`),
          client.get(`${API_BASE_URL}/api/prediction-tracking/stats`)
        ]);
        
        console.log('Received events response:', {
          status: eventsRes.status,
          statusText: eventsRes.statusText,
          headers: eventsRes.headers
        });
        
        console.log('Received stats response:', {
          status: statsRes.status,
          statusText: statsRes.statusText,
          headers: statsRes.headers
        });
        
        const eventsData = eventsRes.data;
        const statsData = statsRes.data;
        
        console.log('Received events data:', eventsData);
        console.log('Received stats data:', statsData);
        
        setEvents(eventsData.events || []);
        setStats(statsData);

        // Generate timeline data from events
        const timelineData = (eventsData.events || []).map((event: Event) => {
          const totalFights = event.fights.length;
          const correctPredictions = event.fights.filter(
            (fight: Fight) => fight.winner === fight.ourPrediction
          ).length;
          const correctFavorites = event.fights.filter(
            (fight: Fight) => fight.winner === fight.bettingFavorite
          ).length;

          return {
            date: event.eventDate,
            successRate: totalFights > 0 ? (correctPredictions / totalFights) * 100 : 0,
            baselineSuccessRate: totalFights > 0 ? (correctFavorites / totalFights) * 100 : 0
          };
        });

        // Sort timeline data chronologically (oldest to newest)
        const sortedTimelineData = [...timelineData].sort((a, b) => 
          new Date(a.date).getTime() - new Date(b.date).getTime()
        );

        setTimeline(sortedTimelineData);
        setError(null);
      } catch (err) {
        console.error('Error fetching prediction data:', err);
        // More detailed error logging
        if (axios.isAxiosError(err)) {
          console.error('Axios error details:', {
            response: err.response?.data,
            status: err.response?.status,
            headers: err.response?.headers,
            request: err.request,
            message: err.message
          });
        }
        setError(err instanceof Error ? `${err.message} - Please check console for details` : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const goHome = () => {
    navigate('/');
  };

  const toggleEvent = (eventName: string) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventName)) {
        newSet.delete(eventName);
      } else {
        newSet.add(eventName);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPercentage = (value: number) => {
    return (value * 100).toFixed(1) + '%';
  };

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-red-600 dark:text-red-400">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="relative bg-white-only dark:bg-dark-background min-h-[calc(100vh-73px)] pt-[73px]">
      {/* Back Button - fixed position relative to container */}
      <div className="fixed top-[120px] z-10 hidden md:block" style={{ left: `max(calc((100vw - 1800px) / 2 + 1rem), 1rem)` }}>
        <ClickableElement
          as="button"
          onClick={goHome}
          className="p-2 rounded-full hover:bg-zinc-150 dark:hover:bg-zinc-800 text-text-secondary dark:text-dark-text-secondary active:scale-95"
        >
          <ArrowLeft className="h-6 w-6" weight="regular" />
        </ClickableElement>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-16 relative">
        {/* Header section */}
        <div className="mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-text-primary dark:text-dark-text-primary mb-8">
            Track predictions
          </h1>
          <p className="text-xl text-text-secondary dark:text-dark-text-secondary">
            Historical record of our UFC fight predictions compared to betting favorites.
          </p>
        </div>

        {/* Stats cards - 3-card layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Success rate */}
          <div className="bg-background-secondary dark:bg-dark-bg-secondary rounded-3xl p-6">
            <h2 className="text-text-secondary dark:text-dark-text-secondary text-sm font-medium mb-4">
              Success Rate
            </h2>
            <div className="flex items-end">
              <span className="text-4xl font-bold text-text-primary dark:text-dark-text-primary">
                {formatPercentage(stats?.accuracy || 0)}
              </span>
            </div>
            {(() => {
              const modelAccuracy = stats?.accuracy || 0;
              const favoriteAccuracy = stats?.favoriteAccuracy || 0;
              const difference = ((modelAccuracy - favoriteAccuracy) * 100).toFixed(1);
              const isPositive = modelAccuracy > favoriteAccuracy;

              return (
                <p className="text-text-tertiary dark:text-dark-text-tertiary text-xs mt-3">
                  <span className={isPositive ?
                    'text-green-600 dark:text-green-400' : 'text-rose-600 dark:text-rose-400'}>
                    {isPositive ? '+' + difference : difference}%
                  </span> vs favorites ({formatPercentage(favoriteAccuracy)})
                </p>
              );
            })()}
          </div>

          {/* Total predictions */}
          <div className="bg-background-secondary dark:bg-dark-bg-secondary rounded-3xl p-6">
            <h2 className="text-text-secondary dark:text-dark-text-secondary text-sm font-medium mb-4">
              Correct Predictions
            </h2>
            <div className="flex items-baseline">
              <span className="text-4xl font-bold text-text-primary dark:text-dark-text-primary">
                {stats?.totalFights ? Math.round(stats.accuracy * stats.totalFights) : 0}
              </span>
              <span className="text-xl text-text-secondary dark:text-dark-text-secondary mx-2">
                of
              </span>
              <span className="text-4xl font-bold text-text-primary dark:text-dark-text-primary">
                {stats?.totalFights || 0}
              </span>
            </div>
            {(() => {
              const modelCorrect = stats?.totalFights ? Math.round(stats.accuracy * stats.totalFights) : 0;
              const favCorrect = stats?.totalFights ? Math.round(stats.favoriteAccuracy * stats.totalFights) : 0;
              const diff = modelCorrect - favCorrect;
              const isPositive = diff >= 0;

              return (
                <p className="text-text-tertiary dark:text-dark-text-tertiary text-xs mt-3">
                  <span className={isPositive ?
                    'text-green-600 dark:text-green-400' : 'text-rose-600 dark:text-rose-400'}>
                    {isPositive ? '+' : ''}{diff}
                  </span> vs baseline ({favCorrect})
                </p>
              );
            })()}
          </div>

          {/* Betting ROI */}
          <div className="bg-background-secondary dark:bg-dark-bg-secondary rounded-3xl p-6">
            <h2 className="text-text-secondary dark:text-dark-text-secondary text-sm font-medium mb-4 flex items-center gap-1.5">
              Betting ROI
              <div className="relative group">
                <Question className="w-3.5 h-3.5 text-text-tertiary dark:text-dark-text-tertiary cursor-help" weight="bold" />
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg bg-zinc-900 dark:bg-zinc-700 text-white text-xs leading-relaxed w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-10">
                  Simulated return on $10 flat bets placed on every model pick across {stats?.roiFights || stats?.totalFights || 0} fights, compared to betting $10 on the favorite each time.
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-900 dark:border-t-zinc-700"></div>
                </div>
              </div>
            </h2>
            {(() => {
              const modelROI = stats?.modelROI ?? 0;
              const favROI = stats?.favoriteROI ?? 0;
              const edge = modelROI - favROI;
              const isPositive = modelROI >= 0;
              return (
                <>
                  <div className="flex items-end">
                    <span className={`text-4xl font-bold ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                      {modelROI >= 0 ? '+' : ''}{modelROI.toFixed(1)}%
                    </span>
                  </div>
                  <p className="text-text-tertiary dark:text-dark-text-tertiary text-xs mt-3">
                    <span className={edge >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
                      {edge >= 0 ? '+' : ''}{edge.toFixed(1)}%
                    </span> vs favorites ({favROI >= 0 ? '+' : ''}{favROI.toFixed(1)}%)
                  </p>
                </>
              );
            })()}
          </div>
        </div>

        {/* Success rate chart - restore original styling */}
        <div className="bg-background-secondary dark:bg-dark-bg-secondary rounded-3xl p-6 mb-8">
          <h2 className="text-sm font-medium text-text-secondary dark:text-dark-text-secondary mb-4">
            Success Rate Over Time
          </h2>
          
          <div className="h-64 w-full">
            {timeline.length >= 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={timeline.length === 1 
                    ? [
                        {
                          ...timeline[0],
                          date: new Date(new Date(timeline[0].date).getTime() - 86400000).toISOString().split('T')[0]
                        },
                        timeline[0]
                      ] 
                    : timeline
                  }
                  margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
                >
                  <defs>
                    <linearGradient id="successRateGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme === 'light' ? '#2976ff' : '#5a96ff'} stopOpacity={0.34} />
                      <stop offset="95%" stopColor={theme === 'light' ? '#2976ff' : '#5a96ff'} stopOpacity={0.06} />
                    </linearGradient>
                    <linearGradient id="baselineGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme === 'light' ? '#ef4444' : '#f87171'} stopOpacity={0.24} />
                      <stop offset="95%" stopColor={theme === 'light' ? '#ef4444' : '#f87171'} stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid 
                    strokeDasharray="2 2" 
                    stroke={theme === 'light' ? '#cbd5e1' : '#475569'} 
                    vertical={false} 
                    strokeWidth={1.5}
                  />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => formatDate(value)}
                    stroke={theme === 'light' ? '#71717A' : '#A1A1AA'}
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      fill: theme === 'light' ? '#71717A' : '#A1A1AA',
                    }}
                    tickLine={false}
                    axisLine={false}
                    dy={10}
                  />
                  <YAxis 
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                    stroke={theme === 'light' ? '#71717A' : '#A1A1AA'}
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      fill: theme === 'light' ? '#71717A' : '#A1A1AA',
                    }}
                    tickLine={false}
                    axisLine={false}
                    dx={-10}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="linear" 
                    dataKey="successRate" 
                    stroke={theme === 'light' ? '#2976ff' : '#5a96ff'} 
                    strokeWidth={2}
                    fill="url(#successRateGradient)"
                    dot={{ r: 3, fill: theme === 'light' ? '#2976ff' : '#5a96ff', strokeWidth: 2, stroke: theme === 'light' ? '#ffffff' : '#111827' }}
                    activeDot={{ r: 5, fill: theme === 'light' ? '#2976ff' : '#5a96ff', strokeWidth: 2, stroke: theme === 'light' ? '#ffffff' : '#111827' }}
                    name="Model performance"
                  />
                  <Area 
                    type="linear" 
                    dataKey="baselineSuccessRate" 
                    stroke={theme === 'light' ? '#ef4444' : '#f87171'} 
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    fill="url(#baselineGradient)"
                    dot={{ r: 3, fill: theme === 'light' ? '#ef4444' : '#f87171', strokeWidth: 2, stroke: theme === 'light' ? '#ffffff' : '#111827' }}
                    activeDot={{ r: 5, fill: theme === 'light' ? '#ef4444' : '#f87171', strokeWidth: 2, stroke: theme === 'light' ? '#ffffff' : '#111827' }}
                    name="Betting favorite baseline"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-text-tertiary dark:text-dark-text-tertiary">
                  Not enough data points to display chart
                </p>
              </div>
            )}
          </div>
          
          <div className="mt-2 flex items-center justify-end gap-4">
            <div className="flex items-center">
              <div className="h-0.5 w-6 bg-blue-500 dark:bg-blue-400 mr-2"></div>
              <span className="text-text-tertiary dark:text-dark-text-tertiary text-xs">
                Model performance
              </span>
            </div>
            <div className="flex items-center">
              <div className="h-0.5 w-6 border-t-2 border-dashed border-red-500 dark:border-red-400 mr-2"></div>
              <span className="text-text-tertiary dark:text-dark-text-tertiary text-xs">
                Baseline (picking favorites)
              </span>
            </div>
          </div>
        </div>

        {/* Event cards */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-text-primary dark:text-dark-text-primary mb-4">
            Event History
          </h2>
          
          <div className="space-y-4">
            {events.map((event) => (
              <div 
                key={event.eventName} 
                className="bg-background-secondary dark:bg-dark-bg-secondary rounded-3xl overflow-hidden"
              >
                <div 
                  className={`flex items-center gap-2 justify-between p-6 ${expandedEvents.has(event.eventName) ? 'border-b border-zinc-150 dark:border-zinc-800' : ''} cursor-pointer`}
                  onClick={() => toggleEvent(event.eventName)}
                >
                  <div className="flex-1 min-w-0 mr-4">
                    <h3 className="text-base font-semibold text-text-primary dark:text-dark-text-primary truncate">
                      {event.eventName}
                    </h3>
                    <p className="text-xs text-text-tertiary dark:text-dark-text-tertiary mt-1">
                      {formatDate(event.eventDate)}
                    </p>
                  </div>
                  <div className="flex items-center shrink-0">
                    <span className="text-sm text-text-secondary dark:text-dark-text-secondary mr-1 whitespace-nowrap">
                      {event.fights.filter(f => f.winner === f.ourPrediction).length} of {event.fights.length} 
                    </span>
                    <div className={`p-2 rounded-full hover:bg-zinc-150 dark:hover:bg-zinc-800 transform transition-all ${expandedEvents.has(event.eventName) ? 'rotate-180' : ''}`}>
                      <CaretDown className="w-4 h-4 text-text-secondary dark:text-dark-text-secondary" weight="bold" />
                    </div>
                  </div>
                </div>
                
                {expandedEvents.has(event.eventName) && (
                  <div className="p-6">
                    <table className="w-full">
                      <thead>
                        <tr className="text-sm text-text-secondary dark:text-dark-text-secondary">
                          <th className="text-left font-medium py-2">Predicted Winner</th>
                          <th className="text-right font-medium py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {event.fights.map((fight, index) => (
                          <tr key={index} className="border-t border-zinc-150 dark:border-zinc-800">
                            <td className="py-3">
                              <div>
                                <p className="text-base font-medium text-text-primary dark:text-dark-text-primary">
                                  {fight.ourPrediction}
                                </p>
                                <p className="text-sm text-text-tertiary dark:text-dark-text-tertiary mt-0.5">
                                  vs {fight.ourPrediction === fight.fighter1 ? fight.fighter2 : fight.fighter1}
                                </p>
                              </div>
                            </td>
                            <td className="text-right py-3">
                              <div className="flex items-center justify-end">
                                {fight.winner === fight.ourPrediction ? (
                                  <>
                                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" weight="regular" />
                                    <span className="ml-2 text-sm font-medium text-green-600 dark:text-green-400">
                                      Correct
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" weight="regular" />
                                    <span className="ml-2 text-sm font-medium text-rose-600 dark:text-rose-400">
                                      Wrong
                                    </span>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictionTracker; 